import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { useToast, toast } from '../UI/Toast';
import Button from '../UI/Button';
import { StepProps } from '../../types';
import { Upload, File, X, CheckCircle, Loader2 } from 'lucide-react';
import { ApiService } from '../../services/api';

interface Step5Data {
  documents: {
    cacCertificate: File | null;
    professionalLicenseCertificate: File | null;
    taxClearanceCertificate: File | null;
    membershipCertificate: File | null;
    validId: File | null;
    passportPhotograph: File | null;
  };
}

type DocKey = keyof Step5Data['documents'];

type UploadState = {
  status: 'idle' | 'uploading' | 'uploaded' | 'error';
  error?: string;
  response?: unknown;
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const Title = styled.h2<{ theme: any }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const Description = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const UploadGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

const DocCard = styled.div<{ theme: any }>`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 1rem;
`;

const DocHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const DocTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DocTitle = styled.h3<{ theme: any }>`
  margin: 0;
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  font-weight: 700;
`;

const DocHint = styled.p<{ theme: any }>`
  margin: 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
  line-height: 1.4;
`;

const RequiredPill = styled.span<{ theme: any }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.primary}12;
  border: 1px solid ${props => props.theme.colors.primary}24;
  flex: 0 0 auto;
`;

const UploadArea = styled.div<{ theme: any; isDragOver: boolean }>`
  border: 2px dashed ${props => props.isDragOver ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: 12px;
  padding: 1.25rem 1rem;
  text-align: center;
  background-color: ${props => props.isDragOver ? props.theme.colors.primary + '10' : props.theme.colors.surface};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary}10;
  }
`;

const UploadIcon = styled.div<{ theme: any }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.5rem;
  color: ${props => props.theme.colors.primary};
`;

const UploadText = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

const UploadSubtext = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  margin: 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileItem = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  margin-top: 0.75rem;
`;

const UploadStatusRow = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
`;

const StatusPill = styled.span<{ theme: any; $variant: 'success' | 'error' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.75rem;
  border: 1px solid ${props =>
    props.$variant === 'success'
      ? `${props.theme.colors.success}33`
      : props.$variant === 'error'
        ? `${props.theme.colors.error}33`
        : `${props.theme.colors.border}`};
  background: ${props =>
    props.$variant === 'success'
      ? `${props.theme.colors.success}12`
      : props.$variant === 'error'
        ? `${props.theme.colors.error}12`
        : `${props.theme.colors.background}`};
  color: ${props =>
    props.$variant === 'success'
      ? props.theme.colors.success
      : props.$variant === 'error'
        ? props.theme.colors.error
        : props.theme.colors.textSecondary};
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const FileIcon = styled.div<{ theme: any }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
`;

const FileDetails = styled.div`
  flex: 1;
`;

const FileName = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-weight: 500;
  margin: 0 0 0.25rem 0;
`;

const FileSize = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.75rem;
  margin: 0;
`;

const RemoveFileButton = styled.button<{ theme: any }>`
  background: none;
  border: none;
  color: ${props => props.theme.colors.error};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.theme.colors.error}20;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ErrorMessage = styled.div<{ theme: any }>`
  background-color: ${props => props.theme.colors.error}10;
  border: 1px solid ${props => props.theme.colors.error}30;
  border-radius: 8px;
  padding: 1rem;
  color: ${props => props.theme.colors.error};
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const SpinningLoader = styled(Loader2)`
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;


const Step5Documents: React.FC<StepProps> = ({ 
  data, 
  onNext, 
  onPrevious, 
  isLastStep, 
  isSubmitting = false, 
  submitError 
}) => {
  const { theme } = useTheme();
  const { showToast } = useToast();
  const [documents, setDocuments] = useState<Step5Data['documents']>({
    cacCertificate: data.documents?.cacCertificate ?? null,
    professionalLicenseCertificate: data.documents?.professionalLicenseCertificate ?? null,
    taxClearanceCertificate: data.documents?.taxClearanceCertificate ?? null,
    membershipCertificate: data.documents?.membershipCertificate ?? null,
    validId: data.documents?.validId ?? null,
    passportPhotograph: data.documents?.passportPhotograph ?? null,
  });
  const [dragOverKey, setDragOverKey] = useState<keyof Step5Data['documents'] | null>(null);
  const [uploads, setUploads] = useState<Record<DocKey, UploadState>>({
    cacCertificate: {
      status: data.documentUploads?.cacCertificate ? 'uploaded' : 'idle',
      response: data.documentUploads?.cacCertificate?.response,
    },
    professionalLicenseCertificate: {
      status: data.documentUploads?.professionalLicenseCertificate ? 'uploaded' : 'idle',
      response: data.documentUploads?.professionalLicenseCertificate?.response,
    },
    taxClearanceCertificate: {
      status: data.documentUploads?.taxClearanceCertificate ? 'uploaded' : 'idle',
      response: data.documentUploads?.taxClearanceCertificate?.response,
    },
    membershipCertificate: {
      status: data.documentUploads?.membershipCertificate ? 'uploaded' : 'idle',
      response: data.documentUploads?.membershipCertificate?.response,
    },
    validId: {
      status: data.documentUploads?.validId ? 'uploaded' : 'idle',
      response: data.documentUploads?.validId?.response,
    },
    passportPhotograph: {
      status: data.documentUploads?.passportPhotograph ? 'uploaded' : 'idle',
      response: data.documentUploads?.passportPhotograph?.response,
    },
  });
  
  const { handleSubmit } = useForm<Step5Data>();

  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): boolean => {
    if (!allowedTypes.includes(file.type)) {
      showToast(toast.error(
        'Invalid File Type',
        `File "${file.name}" is not supported. Please upload only JPEG, PNG, or PDF files.`
      ));
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast(toast.error(
        'File Too Large',
        `File "${file.name}" is too large. Maximum file size is 5MB.`
      ));
      return false;
    }
    return true;
  };

  const setDocFile = (key: keyof Step5Data['documents'], file: File | null) => {
    setDocuments(prev => ({ ...prev, [key]: file }));
  };

  const getUploadContextName = (): string => {
    const type = sessionStorage.getItem('registrationType');
    return type === 'corporate' ? 'CORPORATE_REGISTRATION' : 'INDIVIDUAL_REGISTRATION';
  };

  const uploadOne = async (key: DocKey, file: File, title: string) => {
    const taxId = sessionStorage.getItem('registrationTaxId');
    if (!taxId) {
      setUploads(prev => ({ ...prev, [key]: { status: 'error', error: 'Tax ID not found in session' } }));
      showToast(toast.error('Session Required', 'Please verify your Tax ID and OTP again to continue.'));
      return;
    }

    setUploads(prev => ({ ...prev, [key]: { status: 'uploading' } }));
    try {
      const resp = await ApiService.uploadDocuments({
        taxId,
        ownerTypeName: 'tax_consultant',
        uploadContextName: getUploadContextName(),
        titles: [title],
        files: [file],
      });
      setUploads(prev => ({ ...prev, [key]: { status: 'uploaded', response: resp } }));
      showToast(toast.success('Uploaded', `${title} uploaded successfully.`));
    } catch (e: any) {
      const msg = e?.message || 'Upload failed. Please try again.';
      setUploads(prev => ({ ...prev, [key]: { status: 'error', error: msg } }));
      showToast(toast.error('Upload Failed', msg));
    }
  };

  const handleFileSelect = (key: keyof Step5Data['documents'], selectedFiles: FileList | null, title?: string) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const file = selectedFiles[0];
    if (!validateFile(file)) return;
    setDocFile(key, file);
    void uploadOne(key, file, title || 'Document');
  };

  const handleDragOver = (key: keyof Step5Data['documents'], e: React.DragEvent) => {
    e.preventDefault();
    setDragOverKey(key);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverKey(null);
  };

  const handleDrop = (key: keyof Step5Data['documents'], e: React.DragEvent) => {
    e.preventDefault();
    setDragOverKey(null);
    handleFileSelect(key, e.dataTransfer.files);
  };

  const removeFile = (key: keyof Step5Data['documents']) => {
    setDocFile(key, null);
    setUploads(prev => ({ ...prev, [key]: { status: 'idle' } }));
  };

  const onSubmit = () => {
    const missing = docConfig
      .filter((d) => !documents[d.key] || uploads[d.key]?.status !== 'uploaded')
      .map((d) => d.title);
    if (missing.length) {
      showToast(toast.error(
        'Documents required',
        `Please upload: ${missing.join(', ')}.`,
      ));
      return;
    }

    const uploading = docConfig.some((d) => uploads[d.key]?.status === 'uploading');
    if (uploading) {
      showToast(toast.error('Upload in progress', 'Please wait for all uploads to complete.'));
      return;
    }

    const files = Object.values(documents).filter(Boolean) as File[];
    const documentUploads = Object.fromEntries(
      (Object.keys(uploads) as DocKey[]).map((k) => [
        k,
        uploads[k].status === 'uploaded'
          ? { uploadedAt: new Date().toISOString(), response: uploads[k].response }
          : undefined,
      ]),
    ) as any;

    onNext({ documents, files, documentUploads });
  };

  const docConfig: Array<{
    key: keyof Step5Data['documents'];
    title: string;
    hint: string;
  }> = [
    { key: 'cacCertificate', title: 'Certificate of Incorporation (CAC)', hint: 'Upload your CAC certificate (PDF or image).' },
    { key: 'professionalLicenseCertificate', title: 'Professional License Certificate', hint: 'Upload your valid professional license certificate.' },
    { key: 'taxClearanceCertificate', title: 'Tax Clearance Certificate', hint: 'Upload your most recent tax clearance certificate.' },
    { key: 'membershipCertificate', title: 'Professional Body Membership Certificate', hint: 'Upload your membership certificate for your professional body.' },
    { key: 'validId', title: 'Valid ID', hint: 'National ID, Driver’s License, or International Passport.' },
    { key: 'passportPhotograph', title: 'Passport Photograph', hint: 'Upload a clear passport photograph (JPEG/PNG).' },
  ];

  const missingDocs = docConfig
    .filter((d) => !documents[d.key] || uploads[d.key]?.status !== 'uploaded')
    .map((d) => d.title);
  const hasUploading = docConfig.some((d) => uploads[d.key]?.status === 'uploading');
  const canContinue = missingDocs.length === 0 && !hasUploading && !isSubmitting;

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <HeaderContainer>
        <Title theme={theme}>Document Upload</Title>
        <Description theme={theme}>
          Please upload the required documents to complete your registration. All documents should be clear and legible.
        </Description>
      </HeaderContainer>

      <UploadGrid>
        {docConfig.map((doc) => {
          const file = documents[doc.key];
          const inputId = `file-input-${doc.key}`;
          const isDragOver = dragOverKey === doc.key;
          const uploadState = uploads[doc.key];
          const isUploading = uploadState?.status === 'uploading';

          return (
            <DocCard key={doc.key} theme={theme}>
              <DocHeader>
                <DocTitleWrap>
                  <DocTitle theme={theme}>{doc.title}</DocTitle>
                  <DocHint theme={theme}>{doc.hint}</DocHint>
                </DocTitleWrap>
                <RequiredPill theme={theme}>Required</RequiredPill>
              </DocHeader>

              <UploadArea
                theme={theme}
                isDragOver={isDragOver}
                onDragOver={(e) => !isUploading && handleDragOver(doc.key, e)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => !isUploading && handleDrop(doc.key, e)}
                onClick={() => !isUploading && document.getElementById(inputId)?.click()}
              >
                <UploadIcon theme={theme}>
                  <Upload size={22} />
                </UploadIcon>
                <UploadText theme={theme}>
                  {isUploading ? 'Uploading…' : 'Drag and drop a file here, or click to select'}
                </UploadText>
                <UploadSubtext theme={theme}>
                  JPEG, PNG, PDF (Max 5MB)
                </UploadSubtext>
              </UploadArea>

              <HiddenInput
                id={inputId}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => handleFileSelect(doc.key, e.target.files, doc.title)}
              />

              {file && (
                <FileItem theme={theme}>
                  <FileInfo>
                    <FileIcon theme={theme}>
                      <File size={20} />
                    </FileIcon>
                    <FileDetails>
                      <FileName theme={theme}>{file.name}</FileName>
                      <FileSize theme={theme}>{formatFileSize(file.size)}</FileSize>
                    </FileDetails>
                    {uploadState?.status === 'uploaded' ? (
                      <CheckCircle size={20} color={theme.colors.success} />
                    ) : uploadState?.status === 'uploading' ? (
                      <SpinningLoader size={18} />
                    ) : null}
                  </FileInfo>
                  <RemoveFileButton
                    type="button"
                    theme={theme}
                    onClick={() => removeFile(doc.key)}
                    aria-label={`Remove ${doc.title}`}
                  >
                    <X size={20} />
                  </RemoveFileButton>
                </FileItem>
              )}

              <UploadStatusRow theme={theme}>
                {uploadState?.status === 'uploaded' ? (
                  <StatusPill theme={theme} $variant="success">Uploaded</StatusPill>
                ) : uploadState?.status === 'error' ? (
                  <StatusPill theme={theme} $variant="error">Upload failed</StatusPill>
                ) : uploadState?.status === 'uploading' ? (
                  <StatusPill theme={theme} $variant="muted">
                    <SpinningLoader size={14} /> Uploading
                  </StatusPill>
                ) : (
                  <StatusPill theme={theme} $variant="muted">Not uploaded</StatusPill>
                )}

                {uploadState?.status === 'error' && (
                  <span style={{ color: theme.colors.error, fontWeight: 600, fontSize: '0.8rem' }}>
                    {uploadState.error}
                  </span>
                )}
              </UploadStatusRow>
            </DocCard>
          );
        })}
      </UploadGrid>

      {submitError && (
        <ErrorMessage theme={theme}>
          {submitError}
        </ErrorMessage>
      )}

      <ButtonContainer>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onPrevious}
          disabled={isSubmitting}
        >
          Previous
        </Button>
        <Button 
          type="submit" 
          size="large"
          disabled={!canContinue}
        >
          {isSubmitting ? (
            <>
              <SpinningLoader size={20} />
              Submitting...
            </>
          ) : (
            isLastStep ? 'Complete Registration' : 'Next Step'
          )}
        </Button>
      </ButtonContainer>
    </Form>
  );
};

export default Step5Documents;
